import React, { useEffect, useRef } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router-dom';
import type { Location } from 'react-router';
import { FILE_NOT_FOUND_MODAL_ID, isModalOpen, useModalHandlers } from 'components/modal';
import { FmlxButton } from 'fmlx-common-ui';

export function useNavigationBlock(
  isDirty: boolean,
  shouldBlock: (location?: Location) => React.ReactNode | string | undefined,
) {
  const handlerRef = useRef(shouldBlock);
  const modalShownRef = useRef(false);

  const { showAwaitableModal, hideModal } = useModalHandlers();

  useEffect(() => {
    handlerRef.current = shouldBlock;
  }, [shouldBlock]);

  const blocker = useBlocker(({ nextLocation }) => {
    if (!isDirty || isModalOpen(FILE_NOT_FOUND_MODAL_ID)) {
      return false;
    }

    return !!handlerRef.current(nextLocation);
  });

  useEffect(() => {
    if (blocker.state !== 'blocked' || modalShownRef.current) {
      return;
    }

    modalShownRef.current = true;

    const msg = handlerRef.current(blocker.location);

    if (!msg) {
      modalShownRef.current = false;

      if (blocker.state === 'blocked') {
        blocker.proceed();
      }

      return;
    }

    showAwaitableModal({
      heading: 'Confirm Navigation',
      body: msg,
      okButtonText: 'Leave',
      noButtonText: 'Stay',
      isModalEscapeable: false,
      hideCloseButton: true,
      customFooter: (
        <div className="flex flex-row items-center justify-end w-full h-12 gap-3 px-2 mt-4 border-t border-t-[#CACACA]">
          <FmlxButton
            type="primary"
            variant="outline"
            label="Leave"
            onClick={() => {
              modalShownRef.current = false;
              hideModal();

              if (blocker.state === 'blocked') {
                blocker.proceed();
              }
            }}
          />

          <FmlxButton
            label="Stay"
            onClick={() => {
              modalShownRef.current = false;
              hideModal();

              if (blocker.state === 'blocked') {
                blocker.reset();
              }
            }}
          />
        </div>
      ),
    });
  }, [blocker.state]);

  useBeforeUnload((event) => {
    if (!isDirty) {
      return;
    }

    if (!handlerRef.current()) {
      return;
    }

    event.preventDefault();
    event.returnValue = '';
  });
}
